/**
 * Admin / księgowy view of all B2B invoices.
 * Used by CEO ("Akceptacja faktur B2B") and Accountant ("Rozliczenie faktur B2B").
 *
 * Filters by status (default "submitted" for CEO, "approved" for accountant).
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Spinner } from "@/components/ui/Spinner";
import { Select } from "@/components/ui/Select";
import { useB2BInvoices } from "../hooks/useB2BInvoices";
import { B2BInvoiceStatusBadge } from "../components/B2BInvoiceStatusBadge";
import { MIN_INVOICE_PERIOD, type B2BInvoiceStatus } from "../types";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Wszystkie statusy" },
  { value: "submitted", label: "Złożone — do zatwierdzenia" },
  { value: "approved", label: "Zatwierdzone — do księgowania" },
  { value: "booked", label: "Zaksięgowane" },
  { value: "transferred_for_payment", label: "Przekazane do opłacenia" },
  { value: "paid", label: "Opłacone" },
  { value: "rejected", label: "Odrzucone" },
  { value: "draft", label: "Szkice" },
];

// Combined "year/month" filter. Value is encoded as "YYYY-MM" and split
// before being sent to the backend (which still expects year + month separately).
// Lower bound is MIN_INVOICE_PERIOD (B2B invoices were introduced in May 2026),
// so older months are not offered.
function buildPeriodOptions(): { value: string; label: string }[] {
  const now = new Date();
  const opts: { value: string; label: string }[] = [{ value: "", label: "Wszystkie okresy" }];
  const minIdx = MIN_INVOICE_PERIOD.year * 12 + (MIN_INVOICE_PERIOD.month - 1);
  let idx = now.getFullYear() * 12 + now.getMonth();
  while (idx >= minIdx) {
    const y = Math.floor(idx / 12);
    const m = (idx % 12) + 1;
    const mm = String(m).padStart(2, "0");
    opts.push({ value: `${y}-${m}`, label: `${mm}.${y}` });
    idx--;
  }
  return opts;
}
const PERIOD_OPTIONS = buildPeriodOptions();

function formatPln(value: string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 2,
    // "always" forces a thousands separator for 4-digit pl-PL values; the pl-PL
    // CLDR default (minimumGroupingDigits=2) otherwise skips grouping below 10 000.
    useGrouping: "always",
  }).format(n);
}

interface Props {
  title: string;
  defaultStatus?: B2BInvoiceStatus | "";
  detailPathPrefix?: string;
}

export default function B2BInvoiceAdminListPage({
  title,
  defaultStatus = "",
  detailPathPrefix = "/b2b-invoices",
}: Props) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>(defaultStatus);
  const [period, setPeriod] = useState<string>("");
  const [employee, setEmployee] = useState<string>("");

  // Employee filter is applied client-side, so we can derive the dropdown
  // options from the same query that paints the table.
  const params: Record<string, string> = {};
  if (status) params.status = status;
  if (period) {
    const [y, m] = period.split("-");
    if (y) params.year = y;
    if (m) params.month = m;
  }

  const { data, isLoading } = useB2BInvoices(params);

  const employeeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const inv of data?.data ?? []) {
      if (!seen.has(inv.employee)) seen.set(inv.employee, inv.employee_full_name);
    }
    const sorted = Array.from(seen.entries()).sort(([, a], [, b]) =>
      a.localeCompare(b, "pl"),
    );
    return [
      { value: "", label: "Wszyscy" },
      ...sorted.map(([value, label]) => ({ value, label })),
    ];
  }, [data?.data]);

  const visibleInvoices = useMemo(() => {
    const rows = data?.data ?? [];
    return employee ? rows.filter((inv) => inv.employee === employee) : rows;
  }, [data?.data, employee]);

  return (
    <div className="space-y-4">
      <h1 className="text-xl sm:text-2xl font-semibold">{title}</h1>

      <div className="grid grid-cols-1 gap-3 sm:flex sm:flex-wrap">
        <div className="sm:w-64">
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={STATUS_OPTIONS}
          />
        </div>
        <div className="sm:w-44">
          <Select
            label="Okres"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            options={PERIOD_OPTIONS}
          />
        </div>
        <div className="sm:w-64">
          <Select
            label="Pracownik"
            value={employee}
            onChange={(e) => setEmployee(e.target.value)}
            options={employeeOptions}
          />
        </div>
      </div>

      {isLoading ? (
        <Spinner />
      ) : visibleInvoices.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-600">
          Brak faktur spełniających kryteria.
        </div>
      ) : (
        <>
          {/* Mobile — card list */}
          <ul className="space-y-2 sm:hidden">
            {visibleInvoices.map((inv) => (
              <li key={inv.id}>
                <Link
                  to={`${detailPathPrefix}/${inv.id}`}
                  className="block rounded-lg border border-slate-200 bg-white p-3 shadow-sm active:bg-slate-50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-[var(--color-primary)] leading-tight truncate">
                        {inv.employee_full_name}
                        {inv.employee_is_vat_payer && (
                          <span className="ml-1 text-xs text-emerald-700 font-normal">(VAT)</span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                        <span>{String(inv.period_month).padStart(2, "0")}.{inv.period_year}</span>
                        <span className="text-slate-300">•</span>
                        <span className="font-mono truncate">{inv.invoice_number}</span>
                      </div>
                    </div>
                    <div className="shrink-0">
                      <B2BInvoiceStatusBadge status={inv.status} />
                    </div>
                  </div>
                  <div className="mt-2 text-right text-base font-semibold tabular-nums">
                    {formatPln(inv.amount_gross)}
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop — table */}
          <div className="hidden sm:block overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Pracownik</th>
                  <th className="px-3 py-2 text-left font-medium">Okres</th>
                  <th className="px-3 py-2 text-left font-medium">Numer</th>
                  <th className="px-3 py-2 text-right font-medium">Kwota brutto</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {visibleInvoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="cursor-pointer border-l-2 border-transparent transition-colors hover:border-[var(--color-primary)] hover:bg-slate-50"
                    onClick={() => navigate(`${detailPathPrefix}/${inv.id}`)}
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`${detailPathPrefix}/${inv.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-[var(--color-primary)] hover:underline"
                      >
                        {inv.employee_full_name}
                      </Link>
                      {inv.employee_is_vat_payer && (
                        <span className="ml-1 text-xs text-emerald-700">(VAT)</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {String(inv.period_month).padStart(2, "0")}.{inv.period_year}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number}</td>
                    <td className="px-3 py-2 text-right font-medium">
                      {formatPln(inv.amount_gross)}
                    </td>
                    <td className="px-3 py-2">
                      <B2BInvoiceStatusBadge status={inv.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
